import { Epic } from "redux-observable";
import { of } from "rxjs";
import { catchError, filter, mergeMap } from "rxjs/operators";
import { isActionOf } from "typesafe-actions";
import { Dependencies } from "../../../services/index";
import { BankAccount, Card } from "../../models/billing";
import { RootState } from "../../rootReducer";
import { RootAction } from "../../types";
import {
  createCardAsync,
  createDepositAsync,
  createRecurringDepositAsync,
  fetchBankAccountsAsync,
  fetchCardsAsync,
  linkBankAccountAsync,
} from "./actions";

export const fetchBankAccountsEpic: Epic<
  RootAction,
  RootAction,
  RootState,
  Dependencies
> = (action$, _, { fetchBankAccounts }) =>
  action$.pipe(
    filter(isActionOf(fetchBankAccountsAsync.request)),
    mergeMap(() => {
      return fetchBankAccounts().pipe(
        mergeMap((bankAccounts: BankAccount[]) =>
          of(fetchBankAccountsAsync.success(bankAccounts))
        ),
        catchError((error) => of(fetchBankAccountsAsync.failure(error)))
      );
    })
  );

export const linkBankAccountEpic: Epic<
  RootAction,
  RootAction,
  RootState,
  Dependencies
> = (action$, _, { linkBankAccount }) =>
  action$.pipe(
    filter(isActionOf(linkBankAccountAsync.request)),
    mergeMap(({ payload: { publicToken, accountId } }) => {
      return linkBankAccount(publicToken, accountId).pipe(
        mergeMap((bankAccount: BankAccount) =>
          of(linkBankAccountAsync.success(bankAccount))
        ),
        catchError((error) => of(linkBankAccountAsync.failure(error)))
      );
    })
  );

export const fetchCardsEpic: Epic<
  RootAction,
  RootAction,
  RootState,
  Dependencies
> = (action$, _, { fetchCards }) =>
  action$.pipe(
    filter(isActionOf(fetchCardsAsync.request)),
    mergeMap(() => {
      return fetchCards().pipe(
        mergeMap((cards: Card[]) => of(fetchCardsAsync.success(cards))),
        catchError((error) => of(fetchCardsAsync.failure(error)))
      );
    })
  );

export const createCardEpic: Epic<
  RootAction,
  RootAction,
  RootState,
  Dependencies
> = (action$, _, { createCard }) =>
  action$.pipe(
    filter(isActionOf(createCardAsync.request)),
    mergeMap(({ payload: { token } }) => {
      return createCard(token).pipe(
        mergeMap((card: Card) => of(createCardAsync.success(card))),
        catchError((error) => of(createCardAsync.failure(error)))
      );
    })
  );

export const createDepositEpic: Epic<
  RootAction,
  RootAction,
  RootState,
  Dependencies
> = (action$, _, { createDeposit }) =>
  action$.pipe(
    filter(isActionOf(createDepositAsync.request)),
    mergeMap(({ payload: { accountId, sourceId, amount } }) => {
      return createDeposit(accountId, sourceId, amount).pipe(
        mergeMap((message: string) => of(createDepositAsync.success(message))),
        catchError((error) => of(createDepositAsync.failure(error)))
      );
    })
  );

export const createRecurringDepositEpic: Epic<
  RootAction,
  RootAction,
  RootState,
  Dependencies
> = (action$, _, { createRecurringDeposit }) =>
  action$.pipe(
    filter(isActionOf(createRecurringDepositAsync.request)),
    mergeMap(({ payload: { accountId, sourceId, amount, startDate } }) => {
      return createRecurringDeposit(accountId, sourceId, amount, startDate).pipe(
        mergeMap((message: string) => of(createRecurringDepositAsync.success(message))),
        catchError((error) => of(createRecurringDepositAsync.failure(error)))
      );
    })
  );
